import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const links = [
  { label: "Services", href: "#services" },
  { label: "Approach", href: "#approach" },
  { label: "Pricing", href: "#pricing" },
  { label: "Book", href: "#booking" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${scrolled ? "backdrop-blur-xl bg-ink/70 border-b border-border/50 py-4" : "py-6 bg-transparent border-b border-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link to="/" data-testid="logo" className="serif text-2xl tracking-tight text-bone">
          Legacy<span className="text-gold">.</span>
        </Link>
        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a key={l.label} href={l.href} data-testid={`nav-${l.label.toLowerCase()}`} className="overline text-bone/70 hover:text-bone link-underline transition-colors duration-300">{l.label}</a>
          ))}
          {user ? (
            <div className="flex items-center gap-5">
              <button onClick={() => navigate("/portal")} data-testid="nav-portal" className="overline text-gold link-underline">Portal</button>
              <button onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout" className="overline text-bone/50 hover:text-bone">Logout</button>
            </div>
          ) : (
            <button onClick={() => navigate("/login")} data-testid="nav-login" className="px-6 py-2.5 border border-gold text-gold overline hover:bg-gold hover:text-ink transition-colors duration-300">Client Portal</button>
          )}
        </nav>
        <button className="md:hidden text-bone" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X size={26} /> : <List size={26} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="md:hidden overflow-hidden bg-ink/95 backdrop-blur-xl border-t border-border/50 mt-4">
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => <a key={l.label} href={l.href} onClick={() => setOpen(false)} className="overline text-bone/70">{l.label}</a>)}
              {user ? (
                <>
                  <button onClick={() => { setOpen(false); navigate("/portal"); }} className="overline text-gold text-left">Portal</button>
                  <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="overline text-bone/50 text-left">Logout</button>
                </>
              ) : (
                <button onClick={() => { setOpen(false); navigate("/login"); }} className="overline text-gold text-left">Client Portal</button>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
